import { z } from 'zod';

// Time format validation (HH:MM for shifts)
const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

// Minutes:Seconds format validation (MM:SS for auto closure)
const minutesSecondsRegex = /^[0-5]?[0-9]:[0-5][0-9]$/;

// Shift schema for reusability
const shiftSchema = z.object({
    start: z.string()
        .min(1, { message: 'Start time is required' })
        .refine((val) => timeRegex.test(val), {
            message: 'Invalid start time format (HH:MM)'
        }),
    end: z.string()
        .min(1, { message: 'End time is required' })
        .refine((val) => timeRegex.test(val), {
            message: 'Invalid end time format (HH:MM)'
        }),
});

// Helper function to convert HH:MM to a total number of minutes from a base time.
const timeToMinutes = (time: string): number => {
    if (!timeRegex.test(time)) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const plantValidationSchema = z.object({
    name: z.string().min(3, { message: 'Plant name must be at least 3 characters' }),
    displayName:z.string().min(3, { message: 'Plant display name must be at least 3 characters' }),
    description: z.string().min(5, { message: 'Plant description must be at least 5 characters' }).max(200, { message: 'Description must be less than 200 characters' }),
    autoClosureTime: z.string().min(1, { message: 'Auto closure time is required' }).refine((val) => minutesSecondsRegex.test(val), { message: 'Invalid time format (MM:SS)' }),

    shifts: z
        .array(shiftSchema)
        .min(1, { message: 'At least one shift is required' })
        .superRefine((shifts, ctx) => {
            let hasErrors = false;

            const processedShifts = shifts.map((shift, index) => {
                const startMinutes = timeToMinutes(shift.start);
                let endMinutes = timeToMinutes(shift.end);

                if (shift.start === shift.end && shift.start !== "") {
                     ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "End time cannot be the same as start time",
                        path: [index, 'end'],
                    });
                    hasErrors = true;
                }
                
                // If end time is before start, it crosses midnight. Add 24 hours.
                if (endMinutes < startMinutes) {
                    endMinutes += 24 * 60;
                }

                return {
                    startMinutes,
                    endMinutes,
                    originalIndex: index,
                };
            });
            
            // If individual shifts have errors (like zero-duration), stop before checking overlaps.
            if (hasErrors) return;


            const sortedShifts = [...processedShifts].sort((a, b) => a.startMinutes - b.startMinutes);

            for (let i = 1; i < sortedShifts.length; i++) {
                const prev = sortedShifts[i - 1];
                const current = sortedShifts[i];

                if (current.startMinutes < prev.endMinutes) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Overlaps with Shift ${current.originalIndex + 1}`,
                        path: [prev.originalIndex, 'end'],
                    });
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Overlaps with Shift ${prev.originalIndex + 1}`,
                        path: [current.originalIndex, 'start'],
                    });
                }
            }

            // ===== Stage 3: Validate the 24-hour window with specific error =====
            if (sortedShifts.length > 0) {
                const firstShift = sortedShifts[0];
                const lastShift = sortedShifts[sortedShifts.length - 1];
                const totalDuration = lastShift.endMinutes - firstShift.startMinutes;

                if (totalDuration > 24 * 60) {
                     ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        // **IMPROVED ERROR**: Pinpoint the exact field causing the violation
                        message: "Schedule exceeds 24 hours from the start of the first shift",
                        path: [lastShift.originalIndex, 'end'],
                    });
                }
            }
        }),

    lines: z.string().min(1, { message: 'Lines selection is required' }).refine((val) => val !== '' && val !== 'Select', { message: 'Please select a valid line count' }),
    timezone: z.string().min(1, { message: 'timezone is required' }).refine((val) => val !== '' && val !== 'Select', { message: 'Please select a valid Time zone' }),

});


export type PlantValidationSchema = z.infer<typeof plantValidationSchema>;

export const zodResolver = (schema: z.ZodSchema<any>) => {
    return (values: any) => {
        try {
            const result = schema.safeParse(values);
            if (result.success) {
                return { values: result.data, errors: {} };
            }
            const errors: Record<string, any> = {};
            result.error.issues.forEach((issue) => {
                const path = issue.path.join('.');
                errors[path] = {
                    type: issue.code,
                    message: issue.message,
                };
            });
            return { values: {}, errors };
        } catch (error) {
            console.error("Zod resolver error:", error);
            return { values: {}, errors: {} };
        }
    };
};